<script setup lang="ts">
    import RoundImageWithDefault from "@songfinch/shared/components/RoundImageWithDefault";
    import ExclusiveLabel from "@songfinch/customer/components/artists/ExclusiveLabel.vue";
    import AtCapacityLabel from "@songfinch/customer/components/artists/AtCapacityLabel.vue";
    import "@songfinch/customer/stylesheets/includes/helpers/text-underline-on-hover.pcss";
    import {ArtistDataType, ArtistListItem} from "@songfinch/data-source/types";
    import {computed} from "vue";
    import {useRouter} from "vue-router";
    import vSmoothCorners from "@songfinch/shared/plugins/smooth_corners/smooth_corners_directive.js";
    import PlayButton from "@songfinch/shared/plugins/audio_player/PlayButton.vue";

    const emit = defineEmits(["artist-clicked"]);
    const props = defineProps<{
        artist: ArtistListItem | ArtistDataType;
        disablePlay?: boolean;
    }>();

    const router = useRouter();

    const songData = computed(() => {
        return {
            ...props.artist,
            media_type: "artist_col",
            player_type: "audio",
        };
    });
    const has24HourBadge = computed(() => {
        return props.artist.available_delivery_days?.includes("1");
    });
    const isAtCapacity = computed(() => {
        return props.artist.status === "at_capacity";
    });
    const showPlayButton = computed(() => {
        return !props.disablePlay && props.artist.audio;
    });

    function goToArtist() {
        emit("artist-clicked");
        return router.push(props.artist.link);
    }
</script>

<template>
    <div class="artistCol">
        <div ref="frame" class="frameBox @relative @cursor-pointer artistImageWrapper" @click.stop="$refs.playButton?.toggle() || goToArtist()">
            <div v-if="has24HourBadge" class="tag24 l9">
                24 hR
            </div>
            <RoundImageWithDefault
                v-smooth-corners="{cornerRadius: 20}"
                :class="{overlay: isAtCapacity}"
                :image="artist.image"
                :alt="artist.name"
                class="blackGradientOverlay"
                not-round
            />
            <PlayButton
                v-if="showPlayButton"
                ref="playButton"
                class="!@absolute @right-[12px] @bottom-[12px] tablet:@right-[20px] tablet:@bottom-[20px] @z-20"
                :data="songData"
            />
        </div>
        <button class="@mt-4 @mb-1 l1 textUnderlineOnHover @cursor-pointer @block @w-full" @click.prevent="goToArtist">
            {{artist.name}}
        </button>
        <ExclusiveLabel v-if="artist.exclusivity" class="@mb-1.5"/>
        <br v-if="artist.exclusivity">
        <AtCapacityLabel v-if="isAtCapacity"/>
    </div>
</template>

<style scoped>
    .artistCol {
        max-width: 335px;
        margin: auto;
        width: 100%;
        text-align: center;
        position: relative;

        @screen tablet-only {
            max-width: 377px;
        }

        .frameBox {
            width: 100%;
            height: 100%;
            position: relative;

            .buttonBox {
                position: absolute;
                width: 48px;
                height: 48px;
                bottom: 0;
            }
        }
        .artistImageWrapper {
            :deep(.imgRoundBox) {
                margin: 0 !important;
                position: relative;
                z-index: 2;
            }

            &:after {
                content: "";
                position: absolute;
                left: 50%;
                bottom: 0;
                width: 90%;
                height: 100%;
                transform: translateX(-50%);
                box-shadow: 0 0 2px 0 rgba(16, 16, 14, 0.20), 0 16px 32px -4px rgba(16, 16, 14, 0.15);
            }

            .tag24 {
                position: absolute;
                padding: 2px 6px 2.5px 6px;
                align-items: center;
                top: 15px;
                left: 15px;
                z-index: 3;
                border-radius: 4px;
                background: var(--sfc-white);
                backdrop-filter: blur(20px);
            }
        }
    }
</style>
